import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import PageLayout, { LoadingOverlay } from '~/src/components/PageLayout';
import { AiGemIcon } from '~/src/components/icons';
import { useSnackbar } from 'notistack';
import useFeatureAccesses from '~/src/hooks/useFeatureAccesses';
import { useUpdateProject } from '~/src/entities/project/hooks';
import { EditableTitleInline } from '~/src/components/Inputs/Edit';
import useMst from '~/src/hooks/useMst';
import { safeGetItem, safeRemoveItem } from '~/src/hooks/useLocalStorage';

import {
  useProject,
  transformProjectFields,
  unsanitizeProjectRecord,
  FORMAT_OVERRIDE_PREFIX,
  reformatDateValue,
  isDateFieldType,
} from '~/src/entities/project';
import type { ProjectStackField } from '~/src/entities/project';
import { PopulatePageContent } from './PopulatePageContent';
import { PopulatePageFooter } from './PopulatePageContent/PopulatePageFooter';
import { useUIFlagEnabled } from '~/src/entities/uiFlags';
import { delayMilliseconds } from '~/src/utils/general';
import analyticsService from '~/src/services/analytics';
import { useHistory, useLocation } from 'react-router-dom';
import {
  useDdhmEditEnabled,
  useCurrentOrgFprint,
  useAIFieldMapperEnabled,
  useAnnotationsDateFieldPickerEnabled,
} from '~/src/entities/user';
import { Project } from '~/src/entities/project/types';
import { useLayoutContext } from '~/src/contexts/Layout';
import { PopulateFlowPollingOverlay } from '~/src/components/PopulateFlowPollingOverlay';
import {
  useBulkApproveMappings,
  FieldMappingTaskStatus,
} from '~/src/entities/fieldMapping';
import { LAYOUT_TOAST_TYPES } from '~/src/components/PageLayout/Toasts';
import { SingleMatterSyncProvider } from '~/src/components/SingleMatterSync';

// Create a proper type for the API response
interface ProjectAPIResponse extends Project {
  clio_parent_id?: string;
  new_version_available?: boolean;
  clio_matter_id?: string;
  matter_id?: number;
}

type PopulatePageV2Props = {
  projectId: number;
};

export const PopulatePageV2 = ({ projectId }: PopulatePageV2Props) => {
  const history = useHistory();
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const manageFolderNameParam = urlParams.get('manageFolderName');
  const showRegenerateModalParam = urlParams.get('showRegenerateModal');
  const fromClioDraftParam = urlParams.get('fromClioDraft');

  const [isSyncReload] = useState(() => {
    const wasSyncReload = safeGetItem('populate_sync_reload') === 'true';
    if (wasSyncReload) {
      safeRemoveItem('populate_sync_reload');
    }
    return wasSyncReload;
  });

  const projectQuery = useProject(projectId, {
    cacheTime: 0,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
  });

  const { updateDocument } = useMst((store) => ({
    updateDocument: store.documents.updateDocument,
  }));

  const featureAccesses = useFeatureAccesses();
  const isUnifiedPlatform = featureAccesses['isUnifiedPlatform'];
  const isProgressTrackerEnabled = !!(
    useUIFlagEnabled('progressTracker') && isUnifiedPlatform
  );
  const [isSaving, setIsSaving] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const { mutateAsync: updateProject } = useUpdateProject();
  const snackbar = useSnackbar();
  const orgFprint = useCurrentOrgFprint();
  const isAIFieldMapperEnabled = useAIFieldMapperEnabled();
  const isDateFieldPickerEnabled = useAnnotationsDateFieldPickerEnabled();

  const projectData = useMemo(() => {
    if (!projectQuery.data) return null;
    return transformProjectFields(projectQuery.data);
  }, [projectQuery.data]);

  const projectApiData = projectQuery.data as ProjectAPIResponse | undefined;

  const { approveMappings } = useBulkApproveMappings({
    orgFprint,
    projectId,
    projectData,
    isAIFieldMapperEnabled,
  });

  const { showModal, showToast } = useLayoutContext();
  const isDdhmEditEnabled = useDdhmEditEnabled();
  const [hasShownModal, setHasShownModal] = useState(false);
  const [fieldMappingError, setFieldMappingError] = useState<{
    message: string;
    failedStage?: string;
  } | null>(null);
  const [showNoAutoFillBanner, setShowNoAutoFillBanner] = useState<
    boolean | null
  >(null);

  const isDoneLoading = !projectQuery.isLoading;

  const onBack = () => history.push('/clients');

  const handleSaveTitle = async (newTitle: string) => {
    const success = await updateDocument(projectId, { title: newTitle });
    const isDocumentSet = (projectData?.stack_data?.documents?.length ?? 0) > 1;
    const documentType = isDocumentSet ? 'Document set' : 'Document';

    if (success) {
      await projectQuery.refetch();
      showToast(LAYOUT_TOAST_TYPES.success, {
        message: `${documentType} "${newTitle}" renamed successfully.`,
      });
    } else {
      showToast(LAYOUT_TOAST_TYPES.error, {
        message: `${documentType} "${projectData?.title}" could not be renamed`,
      });
      throw new Error('Failed to rename document set or document.');
    }
  };

  const pageProps = {
    showNavigation: false,
    headerProps: {
      showCloseButton: true,
      showAvatar: false,
      title:
        projectData && isAIFieldMapperEnabled ? (
          <EditableTitleInline
            title={projectData.title}
            onSave={handleSaveTitle}
          />
        ) : (
          projectData?.title || ''
        ),
      onClose: () => {
        analyticsService.track('Back Clicked', {
          page: 'populate_page',
          location: 'header',
        });
        onBack();
      },
    },
  };

  const methods = useForm({
    values: projectData ? projectData.stack_saved_data : {},
  });

  // Build fmt: overrides and normalize date values so stack_saved_data
  // always has values formatted in the correct date format.
  const getDateFieldOverrides = (
    formValues: Record<string, any>,
  ): Record<string, string> => {
    const fields: ProjectStackField[] = projectData?.stack_data?.fields ?? [];
    const overrides: Record<string, string> = {};
    for (const f of fields) {
      if (isDateFieldType(f.variable_type) && f.fid) {
        const fmtKey = `${FORMAT_OVERRIDE_PREFIX}${f.fid}`;
        // User override (pencil icon) takes precedence over annotation format
        const userOverride = formValues[fmtKey];
        const effectiveFormat = userOverride || f.date_format;
        if (!effectiveFormat) continue;

        overrides[fmtKey] = effectiveFormat;
        const currentValue = formValues[f.fid];
        if (currentValue) {
          const formatted = reformatDateValue(currentValue, effectiveFormat);
          if (formatted && formatted !== currentValue) {
            overrides[f.fid] = formatted;
          }
        }
      }
    }
    return overrides;
  };

  const onGenerate = async () => {
    try {
      setIsGenerating(true);

      /*
      Pause momentarily because there is a slight delay for the change on the screen
      to be reflected in the internal state of react-hook-form.
      This is to prevent the last change on the screen from being missing in the
      internal state of react-hook-form when the generate button is clicked
      (i.e. when the payload for API call is retreieved from the internal state of react-hook-form)
      */
      await delayMilliseconds(1000);

      // Approve AI-generated field mappings before generating
      await approveMappings();

      const formValues = methods.getValues();
      const dateOverrides = isDateFieldPickerEnabled
        ? getDateFieldOverrides(formValues)
        : {};
      await updateProject({
        projectId,
        stack_saved_data: unsanitizeProjectRecord({
          ...formValues,
          ...dateOverrides,
        }),
      });
      history.push(`/drafting/${projectId}`);
    } catch (error) {
      snackbar.enqueueSnackbar(
        'Something went wrong, please try again later.',
        {
          variant: 'error',
        },
      );
      setIsGenerating(false);
    }
  };

  const noErrors = projectData;

  const handleOnCancel = () => {
    analyticsService.track('Back Clicked', {
      page: 'populate_page',
      location: 'footer',
    });
    onBack();
  };

  const shouldShowRegenerateModal = useCallback(() => {
    try {
      const projectData = projectQuery.data as ProjectAPIResponse;

      // Don't show if modal was already shown
      if (hasShownModal) {
        return false;
      }

      // Don't show if project data is not loaded yet
      if (!projectData || projectQuery.isLoading) {
        return false;
      }

      // Must have DDHM_EDIT enabled and showRegenerateModal=true
      const basicConditions =
        isDdhmEditEnabled && showRegenerateModalParam === 'true';

      if (!basicConditions) {
        return false;
      }

      // If coming from Clio Draft (Regenerate button), only show modal if there's a new version
      if (fromClioDraftParam === 'true') {
        return projectData?.new_version_available === true;
      }

      // If coming from Clio Manage, show modal regardless of version
      return true;
    } catch (error) {
      console.error('Error checking modal conditions:', error);
      return false;
    }
  }, [
    isDdhmEditEnabled,
    showRegenerateModalParam,
    fromClioDraftParam,
    projectQuery.data,
    projectQuery.isLoading,
    hasShownModal,
  ]);

  const handleShowRegenerateModal = useCallback(() => {
    const projectData = projectQuery.data as ProjectAPIResponse;
    showModal('regenerateModal', {
      documentSetName: manageFolderNameParam || 'Folder',
      newVersionAvailable: projectData?.new_version_available || false,
      fromClioDraft: fromClioDraftParam === 'true',
      clioMatterId: projectData?.clio_matter_id,
      clioParentId: projectData?.clio_parent_id,
    });
    setHasShownModal(true);
  }, [showModal, manageFolderNameParam, projectQuery.data, fromClioDraftParam]);

  const fieldMappingTaskId =
    projectData?.field_mapping?.status === FieldMappingTaskStatus.IN_PROGRESS
      ? projectData?.field_mapping?.task_id
      : null;

  const handleFieldMappingComplete = useCallback(
    (error?: { message: string; failedStage?: string }) => {
      if (error && error.failedStage === 'FIELD_MAPPING') {
        setFieldMappingError(error);
      }
      projectQuery.refetch();
    },
    [projectQuery],
  );

  // Check if any fields have been auto-filled via mappings
  useEffect(() => {
    if (
      isAIFieldMapperEnabled &&
      projectData &&
      !fieldMappingError &&
      projectData.field_mapping &&
      [
        FieldMappingTaskStatus.COMPLETED,
        FieldMappingTaskStatus.INVALID,
        FieldMappingTaskStatus.DISABLED,
      ].includes(projectData.field_mapping.status)
    ) {
      const hasAnyAutoFilledField = projectData.stack_data.fields.some(
        (field) =>
          field.field_mapping_metadata?.source_field ||
          field.core_field_source?.source_field,
      );

      if (hasAnyAutoFilledField) {
        setShowNoAutoFillBanner(false);
      } else if (showNoAutoFillBanner === null) {
        // Only show auto fill banner for once on this page
        // Do not show again when user manually update the field, since this is a on-purpose change, thus warning is not needed
        setShowNoAutoFillBanner(true);
      }
    }
  }, [
    isAIFieldMapperEnabled,
    projectData,
    fieldMappingError,
    showNoAutoFillBanner,
  ]);

  // Track regeneration initiation regardless of modal visibility
  useEffect(() => {
    const projectData = projectQuery.data as ProjectAPIResponse;
    const basicConditions =
      isDdhmEditEnabled &&
      showRegenerateModalParam === 'true' &&
      projectData &&
      !projectQuery.isLoading;

    if (basicConditions && !hasShownModal) {
      const isFromClioDraft = fromClioDraftParam === 'true';

      // Track the regeneration attempt regardless of modal visibility
      analyticsService.track('Regenerate Initiated', {
        page: 'populate_page',
        project_id: projectId,
        source: isFromClioDraft ? 'clio_draft' : 'clio_manage',
        from_clio_draft: isFromClioDraft,
        new_version_available: projectData?.new_version_available || false,
        clio_matter_id: projectData?.clio_matter_id,
        clio_parent_id: projectData?.clio_parent_id,
      });
    }

    if (shouldShowRegenerateModal()) {
      handleShowRegenerateModal();
    }
  }, [
    shouldShowRegenerateModal,
    handleShowRegenerateModal,
    isDdhmEditEnabled,
    showRegenerateModalParam,
    projectQuery.data,
    projectQuery.isLoading,
    hasShownModal,
    fromClioDraftParam,
    projectId,
  ]);

  if (fieldMappingTaskId) {
    return (
      <PopulateFlowPollingOverlay
        taskId={fieldMappingTaskId}
        onComplete={handleFieldMappingComplete}
        pageProps={pageProps}
      />
    );
  }

  if (noErrors && isDoneLoading)
    return (
      <SingleMatterSyncProvider clioMatterId={projectApiData?.clio_matter_id}>
        <PageLayout
          disablePadding
          {...pageProps}
          footer={
            <PopulatePageFooter
              isGenerating={isGenerating}
              isSaving={isSaving}
              onCancel={handleOnCancel}
              onGenerate={onGenerate}
            />
          }
        >
          <FormProvider {...methods}>
            <PopulatePageContent
              methods={methods}
              project={projectData}
              isClioQuestionnaireEnabled={
                !!featureAccesses.isClioQuestionnaireEnabled
              }
              isProgressTrackerEnabled={isProgressTrackerEnabled}
              onSaving={() => setIsSaving(true)}
              onSaved={() => setIsSaving(false)}
              onGenerate={onGenerate}
              isGenerating={isGenerating}
              matterId={projectApiData?.matter_id}
              clioMatterId={projectApiData?.clio_matter_id}
              fieldMappingError={fieldMappingError}
              onDismissFieldMappingError={() => setFieldMappingError(null)}
              showNoAutoFillBanner={showNoAutoFillBanner === true}
              onDismissNoAutoFillBanner={() => setShowNoAutoFillBanner(false)}
            />
          </FormProvider>
        </PageLayout>
      </SingleMatterSyncProvider>
    );

  if (!isDoneLoading) {
    const loadingTitle: React.ReactNode = isSyncReload ? (
      <span className="flex items-center justify-center gap-2">
        <AiGemIcon className="h-5 w-5 flex-shrink-0" />
        <span>Auto-filling document fields (2 of 2)...</span>
      </span>
    ) : (
      'Loading details'
    );
    const loadingSubtitle = isSyncReload ? '' : 'Please wait';

    return (
      <PageLayout {...pageProps}>
        <LoadingOverlay title={loadingTitle} subtitle={loadingSubtitle} />
      </PageLayout>
    );
  }

  return <PageLayout {...pageProps}>Error</PageLayout>;
};
